<script setup lang="ts">
import type { IGetAlbumFilters } from "@/api"
import { useQueryClient } from "@tanstack/vue-query"
import { useElementVisibility, useLocalStorage, useUrlSearchParams, useWindowScroll } from "@vueuse/core"
import { computed, nextTick, ref, useTemplateRef, watch, watchEffect } from "vue"
import { useGetAlbum, useGetEventVault, useGetWelcomeVideo, useListAlbums } from "@/api"
import { useListInformationItems } from "@/api/listInformationItems"
import { AlbumSelectorCollapsible, EventAdditionalInformationItems, EventGuestbook, EventGuestGallery, FreemiumBanner, GalleryHeroImage, GuestEmptyStatePanel, GuestEventFloatingActions, GuestUnlockAlbumModal, InstallBanner, MainGalleryHiddenMessage, Uploader } from "@/components"
import { Button, Drawer, TabControl } from "@/components/ui"
import { useConvertedRouteParam, useFilters, useGalleryPermissions, useGuestAlbumUnlock, useGuestEventSection, useModal, usePagination, useTranslation } from "@/composables"
import { GuestLayout } from "@/layouts"
import { useEventVaultStore } from "@/stores/eventVaultStore"
import { EventLinksDrawerKey, UploadDrawerKey } from "@/symbols"
import { GuestPageTab } from "@/types"

definePage({
  meta: {
    requiresCookies: true,
    pageLayout: "single",
  },
})

const { t } = useTranslation()

const queryClient = useQueryClient()

const { toggle: toggleUploader, isActive: isUploaderActive } = useModal(UploadDrawerKey)
const { toggle: toggleEventLinksDrawer } = useModal(EventLinksDrawerKey)

const queryString = useUrlSearchParams<{ album?: unknown, upload?: unknown, tab?: unknown }>()

const eventVaultStore = useEventVaultStore()

const eventVaultId = useConvertedRouteParam("id", "/guest/[id]/", value => Number(value))
const albumId = ref<number | undefined>()

const pendingApprovalBannerDismissed = useLocalStorage<boolean>("pending-approval-banner-dismissed", false)

const mainSectionRef = useTemplateRef<HTMLDivElement | null>("mainSectionRef")
const albumSelectorWrapRef = useTemplateRef<HTMLDivElement | null>("albumSelectorWrapRef")

const isAlbumSelectorInView = useElementVisibility(albumSelectorWrapRef, { threshold: 0.08 })

// `y` is vertical scroll offset from useWindowScroll. When it changes, the guest scrolled; if the album
// strip was pinned (Folder FAB), clear sticky so the strip scrolls with the page again.
const isAlbumSelectorSticky = ref(false)
const { y: verticalScrollOffset } = useWindowScroll()
watch(verticalScrollOffset, () => {
  if (isAlbumSelectorSticky.value)
    isAlbumSelectorSticky.value = false
})

const SCROLL_THRESHOLD = 500
const showFloatingActions = computed(() => verticalScrollOffset.value > SCROLL_THRESHOLD)

function scrollToMainSection(behavior: ScrollBehavior = "smooth") {
  nextTick(() => mainSectionRef.value?.scrollIntoView({ behavior, block: "start" }))
}

const { filters: albumFilters } = useFilters<IGetAlbumFilters>({ include: ["settings", "zip"] })

const { data: eventVault, isLoading: isLoadingEvent } = useGetEventVault(eventVaultId, { include: ["zip"] })
const { data: welcomeVideoMedia, isLoading: isLoadingWelcomeVideo } = useGetWelcomeVideo(eventVaultId, undefined, { enabled: Boolean(!albumId.value) })
const { data: album, isLoading: isLoadingAlbum } = useGetAlbum(eventVaultId, albumId, albumFilters)
const { data: albums, isLoading: isLoadingAlbums } = useListAlbums(eventVaultId)

const { filters: informationItemFilters } = useFilters({ page: 1, perPage: 5 })
const { data: informationItems, pageCount: informationItemsPageCount } = useListInformationItems(eventVaultId, informationItemFilters)
const informationItemsPagination = usePagination({ initialPerPage: 5, pageCount: informationItemsPageCount })
watchEffect(() => {
  informationItemFilters.page = informationItemsPagination.page.value
})

const {
  isUploadsEnabled,
  isMainGalleryEnabled,
  isVideoUploadsEnabled,
  showFreemiumBanner,
  isGuestbookEnabled,
  isZipDownloadAllowed,
  isCurrentAlbumLocked,
} = useGalleryPermissions(eventVault, album, { isHost: () => eventVaultStore.isHost ?? false })

const {
  activeTab,
  mainSectionTabs,
} = useGuestEventSection({ queryParams: queryString })

const isHeroLoading = computed<boolean>(() => isLoadingEvent.value || (albumId.value && isLoadingAlbum.value) || isLoadingWelcomeVideo.value)

const isGalleryUnavailable = computed<boolean>(
  () =>
    !isMainGalleryEnabled.value
    && !albumId.value
    && !isLoadingEvent.value
    && !isLoadingAlbums.value
    && !eventVaultStore.isHost,
)

const zipDownloadHref = computed<string | undefined>(() => {
  const url = album.value?.zipSummary?.downloadUrl ?? eventVault.value?.zipSummary?.downloadUrl

  if (!url || !isZipDownloadAllowed.value)
    return undefined

  return url
})

const showAlbumSelectorToggle = computed<boolean>(() => {
  const hasAvailableAlbums = Boolean(eventVault.value?.eventVaultSettings?.albumsEnabled && albums.value?.length)

  if (!hasAvailableAlbums)
    return false

  return isAlbumSelectorSticky.value || !isAlbumSelectorInView.value
})

function selectAlbumById(id: number) {
  albumId.value = id
  queryString.album = id

  if (isAlbumSelectorSticky.value)
    scrollToMainSection()
}

const {
  unlockPendingAlbum,
  onAlbumSelect,
  onGuestAlbumUnlocked,
  openUnlockModalForAlbum,
  clearUnlockPending,
} = useGuestAlbumUnlock({ eventVaultId, isHost: () => eventVaultStore.isHost ?? false, selectAlbumById, scrollToMainSection })

function selectTab(tab: GuestPageTab, scrollMainSection: boolean = false) {
  activeTab.value = tab
  if (scrollMainSection)
    scrollToMainSection()
}

async function onUploadMediaSuccess() {
  pendingApprovalBannerDismissed.value = false
  await queryClient.invalidateQueries({ queryKey: ["listMedia"] })
}

watchEffect(() => {
  if (!isUploaderActive.value && queryString.upload && typeof queryString.upload === "string") {
    toggleUploader()
  }
})
</script>

<template>
  <GuestLayout is-navbar-minimal is-footer-bottom-extended hide-floating-actions>
    <GalleryHeroImage
      :is-loading="isHeroLoading"
      :event-vault="eventVault"
      :album="album"
      :welcome-video="welcomeVideoMedia?.[0]"
      :is-uploads-enabled="isUploadsEnabled"
      :zip-download-url="zipDownloadHref"
      @upload="toggleUploader"
      @scroll-to-gallery="scrollToMainSection"
    />

    <div ref="mainSectionRef" class="min-h-[75vh]">
      <FreemiumBanner v-if="showFreemiumBanner" />
      <InstallBanner v-else with-container />

      <div v-if="informationItems?.length" class="container mx-auto">
        <EventAdditionalInformationItems
          :items="informationItems"
          :title="t('event_additional_information_items.title')"
          :description="t('event_additional_information_items.description')"
          collapsible
          :pagination="informationItemsPagination"
          :show-pagination="(informationItemsPageCount ?? 0) > 1"
          @pagination-next="informationItemsPagination.next"
          @pagination-previous="informationItemsPagination.previous"
        />
      </div>

      <div v-if="eventVaultId && isGuestbookEnabled" class="container mx-auto pt-3 pb-1">
        <TabControl
          v-model="activeTab"
          :tabs="mainSectionTabs"
          :tablist-aria-label="t('guest_event_index.content_tabs_aria_label')"
          data-test="guest-event-main-tabs"
          tab-data-test-prefix="guest-event-main-tab"
        />
      </div>

      <AlbumSelectorCollapsible
        v-if="eventVault?.eventVaultSettings?.albumsEnabled && albums?.length"
        ref="albumSelectorWrapRef"
        v-model="albumId"
        :albums="albums"
        :hide-main-gallery="eventVault.eventVaultSettings?.hideMainGallery && !eventVaultStore.isHost"
        :is-loading="isLoadingAlbums"
        :event-cover-image="eventVault.coverImageUrl"
        class="transition-all duration-300 ease-out will-change-transform"
        :class="[isAlbumSelectorSticky ? 'sticky top-0 z-50 shadow-2xl translate-y-0' : '-translate-y-1']"
        @select="onAlbumSelect"
      />

      <div v-if="eventVaultId" class="container mx-auto">
        <MainGalleryHiddenMessage
          v-if="isGalleryUnavailable"
          :page-message="t('main_gallery_hidden_message.gallery_message')"
        />

        <GuestEmptyStatePanel
          v-else-if="isCurrentAlbumLocked"
          icon="FolderLock"
          :show-icon-badge="false"
          data-test="guest-locked-album-prompt"
        >
          <template #title>
            {{ t("guest_event_index.locked_album_title") }}
          </template>
          <template #description>
            {{ t("guest_event_index.locked_album_description") }}
          </template>
          <template #actions>
            <Button
              prefix-icon="FolderLock"
              class="w-full max-w-xs sm:w-auto"
              data-test="guest-locked-album-unlock-button"
              @click="openUnlockModalForAlbum(album)"
            >
              {{ t("guest_event_index.unlock_album_button") }}
            </Button>
          </template>
        </GuestEmptyStatePanel>

        <EventGuestGallery
          v-else-if="activeTab === GuestPageTab.gallery"
          :event-vault-id="eventVaultId"
          :event-album-id="albumId"
          @upload="toggleUploader"
        />

        <EventGuestbook
          v-else-if="activeTab === GuestPageTab.guestbook"
          :event-vault-id="eventVaultId"
          :event-album-id="albumId"
          @back-to-gallery="() => selectTab(GuestPageTab.gallery)"
        />
      </div>
    </div>

    <GuestEventFloatingActions
      v-model:active-tab="activeTab"
      v-model:albums-sticky="isAlbumSelectorSticky"
      :visible="showFloatingActions"
      :albums-shortcut-visible="showAlbumSelectorToggle"
      :share-link-visible="eventVaultStore.isHost"
      @share-link-click="toggleEventLinksDrawer"
    />
  </GuestLayout>

  <Drawer
    :name="UploadDrawerKey"
    :title="`Upload to ${album?.title ?? 'Gallery'}`"
    keep-alive
    @open="() => queryString.upload = true"
    @close="() => delete queryString.upload"
  >
    <Uploader
      auto-proceed
      :can-upload-videos="isVideoUploadsEnabled"
      :disabled="eventVault?.eventVaultSettings?.hideMainGallery && !albumId"
      :direct-uploads="eventVault?.directUpload"
      @success="onUploadMediaSuccess"
    />
  </Drawer>

  <GuestUnlockAlbumModal
    :event-vault-id="eventVaultId"
    :album="unlockPendingAlbum"
    @close="clearUnlockPending"
    @unlocked="onGuestAlbumUnlocked"
  />
</template>
